import React from 'react';

import { cn } from '@/lib/utils';
import { SELECTED } from './selection';

export interface IconTileOption<T> {
    value: T;
    label: string;
    hint?: string;
    icon: React.ReactNode;
}

interface IconTileChoiceProps<T> {
    value: T | undefined;
    options: IconTileOption<T>[];
    onChange: (value: T) => void;
    columns?: 3 | 4 | 5;
    /** Extra class on every tile, for illustrations that react to hover. */
    tileClassName?: string;
}

const COLUMNS = { 3: 'grid-cols-3', 4: 'grid-cols-4', 5: 'grid-cols-3 sm:grid-cols-5' } as const;

/**
 * A grid of illustrated tiles where one is chosen — a grey line drawing in a
 * rounded tile with its name under it; the chosen one softly outlined, its
 * hint shown under the grid.
 *
 * For choices a person recognises faster by sight than by name: where the
 * camera stands, how it moves.
 */
export function IconTileChoice<T extends string>({ value, options, onChange, columns = 3, tileClassName }: IconTileChoiceProps<T>) {
    const chosen = options.find((o) => o.value === value);
    return (
        <div>
            <div className={cn('grid gap-2', COLUMNS[columns])} role="radiogroup">
                {options.map((o) => {
                    const active = o.value === value;
                    return (
                        <button
                            key={o.value}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            title={o.hint}
                            onClick={() => onChange(o.value)}
                            className={cn(
                                'flex cursor-pointer flex-col items-center gap-1 rounded-lg border px-1.5 pb-2 pt-2.5 text-center transition-colors',
                                active ? SELECTED : 'border-input bg-background hover:bg-accent/40',
                                tileClassName,
                            )}
                        >
                            <span className={cn('flex h-14 w-full items-center justify-center', active ? 'text-foreground' : 'text-muted-foreground')}>
                                {o.icon}
                            </span>
                            <span className={cn('text-xs leading-tight', active ? 'font-medium text-foreground' : 'text-muted-foreground')}>{o.label}</span>
                        </button>
                    );
                })}
            </div>
            {chosen?.hint && <p className="mt-1.5 text-xs text-muted-foreground">{chosen.hint}</p>}
        </div>
    );
}
